import { useState, useRef, useCallback, useEffect } from 'react';
import { useAdmin } from '@/context/AdminContext';
import { uploadToR2 } from '@/lib/r2Upload';
import { compressImage } from '@/lib/imageCompression';
import { supabase } from '@/lib/supabase';

// Cache global em memória para evitar múltiplas queries ao Supabase
const imageCache = new Map<string, { url: string; ts: number }>();

interface EditableImageProps {
  src: string;
  alt: string;
  imageKey: string;
  className?: string;
  containerClassName?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  onImageChange?: (newUrl: string) => void;
}

export default function EditableImage({
  src,
  alt,
  imageKey,
  className = '',
  containerClassName = '',
  style,
  children,
  onImageChange,
}: EditableImageProps) {
  const { isAdmin } = useAdmin();
  const [currentSrc, setCurrentSrc] = useState(src);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Busca a imagem do Supabase ao montar para sincronizar entre dispositivos
  useEffect(() => {
    let cancelled = false;
    async function fetchOverride() {
      try {
        // Tenta cache em memória primeiro (mais rápido que localStorage)
        const memCached = imageCache.get(imageKey);
        if (memCached && !cancelled) {
          setCurrentSrc(memCached.url);
        }

        // Tenta localStorage
        const cached = localStorage.getItem(`jb_img_${imageKey}`);
        if (cached && !cancelled) {
          setCurrentSrc(cached);
        }

        // Pequeno delay aleatório para evitar flood de queries simultâneas
        await new Promise((r) => setTimeout(r, Math.random() * 300));

        // Busca a versão oficial do Supabase
        const { data } = await supabase
          .from('site_images')
          .select('url, updated_at')
          .eq('image_key', imageKey)
          .maybeSingle();

        if (!cancelled && data?.url) {
          setCurrentSrc(data.url);
          localStorage.setItem(`jb_img_${imageKey}`, data.url);
          imageCache.set(imageKey, { url: data.url, ts: Date.now() });
        }
      } catch {
        const local = localStorage.getItem(`jb_img_${imageKey}`);
        if (local && !cancelled) setCurrentSrc(local);
      }
    }
    fetchOverride();
    return () => { cancelled = true; };
  }, [imageKey, src]);

  const handleClickEdit = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      setUploading(true);
      setProgress(10);

      try {
        setProgress(20);
        // Yield para não bloquear a thread principal durante compressão
        await new Promise((r) => setTimeout(r, 0));
        const compressed = await compressImage(file, {
          maxWidth: 1600,
          maxHeight: 1600,
          quality: 0.8,
          mimeType: 'image/jpeg',
        });

        setProgress(50);
        const result = await uploadToR2(compressed, 'site-images');

        setProgress(80);
        // Salva no Supabase para sincronizar entre dispositivos
        await supabase
          .from('site_images')
          .upsert(
            { image_key: imageKey, url: result.url, updated_at: new Date().toISOString() },
            { onConflict: 'image_key' }
          );

        setProgress(90);
        localStorage.setItem(`jb_img_${imageKey}`, result.url);
        imageCache.set(imageKey, { url: result.url, ts: Date.now() });
        setCurrentSrc(result.url);
        onImageChange?.(result.url);
        setProgress(100);

        setTimeout(() => {
          setUploading(false);
          setProgress(0);
        }, 800);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Erro no upload';
        console.error('[EditableImage]', msg);
        alert(`Falha ao enviar imagem: ${msg}`);
        setUploading(false);
        setProgress(0);
      }
    },
    [imageKey, onImageChange]
  );

  return (
    <div className={containerClassName} style={style}>
      <img
        src={currentSrc}
        alt={alt}
        className={className}
        loading="lazy"
        decoding="async"
      />

      {children}

      {isAdmin && !uploading && (
        <button
          onClick={handleClickEdit}
          className="absolute top-2 right-2 z-20 flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-[10px] font-semibold rounded-md hover:bg-red-700 transition-colors shadow-lg"
          title="Trocar imagem"
        >
          <i className="ri-edit-line" />
          EDITAR
        </button>
      )}

      {isAdmin && uploading && (
        <div className="absolute inset-0 z-20 bg-black/60 flex flex-col items-center justify-center gap-2">
          <div className="w-32 h-1.5 bg-white/30 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-400 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="text-white text-xs font-medium">{progress}%</span>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
}